/**
 * Shared `elementName` / `section` vocabulary for every capture SDK flow: SSN Card,
 * Legal ID (both the Veriff and Regula variants) and Face Verification. The vocabulary is
 * what's shared — each flow still owns its own stage -> section map and decides when an
 * Outcome is warranted. Names a single flow happens to be the only user of are marked
 * as such rather than split into a table of their own.
 *
 * Constants only, no imports: the emit helpers that consume these live in `events.ts`
 * alongside the other event wrappers.
 *
 * Two rules that constrain warehouse queries:
 *  - Names are NOT module-prefixed, so group by `(context.app.module, elementName)`.
 *  - A `Client Outcome` reuses the `elementName` of whatever it concludes (see
 *    {@link SCREEN_NAMES}), making the view -> outcome funnel a self-join; `event` and
 *    `elementType` tell the pair apart. Every flow does this, verification included.
 *
 * The naming convention itself (Segment object–action) is in the analytics ADR.
 */

/** elementNames used by more than one event kind — declared once so they can't drift. */
const SCREEN_NAMES = {
  SESSION: "verification_session_started",
  INFO_SCREEN: "info_screen",
  DOCUMENT_CAPTURE_SCREEN: "document_capture_screen",
  PROCESSING_SCREEN: "processing_screen",
  /** Not a screen: the camera stream went live. Every capture flow reports it. */
  CAMERA_STREAM_STARTED: "camera_stream_started",
  /** The post-processing review gate (Confirm / Retake) — Legal ID and SSN Card
   *  (VEX-7846). */
  CONFIRMATION_SCREEN: "confirmation_screen",
  /** Face Verification only — the camera/motion permission gate. */
  CAMERA_PERMISSION_SCREEN: "camera_permission_screen",
  /** Face Verification only — the fullscreen face scan (iProov's own surface). */
  FACE_CAPTURE_SCREEN: "face_capture_screen",
} as const;

/** `section` values — the funnel stage an element belongs to. */
export const DOCUMENT_CAPTURE_SECTIONS = {
  /** Pre-render: the handshake with the launching host. */
  INIT: "init",
  INFO: "info",
  /** The document capture surface (the vendor reader/frame). */
  DOCUMENT_CAPTURE: "document_capture",
  /** Face Verification only — the camera/motion permission gate, its own stage so an
   *  outcome ending there is visible rather than buried in the capture stage. */
  CAMERA_PERMISSION: "camera_permission",
  /** Face Verification only — the face scan. Distinct from `document_capture`. */
  CAPTURE: "capture",
  /** Upload + backend verification after a clean capture. */
  PROCESSING: "processing",
  /** Review extracted attributes, Confirm or Retake — Legal ID and SSN Card. */
  CONFIRMATION: "confirmation",
} as const;

export const DOCUMENT_CAPTURE_EVENTS = {
  VIEWED: {
    /** Handshake succeeded, identity bound — top of funnel. Not a rendered screen. */
    SESSION_STARTED: SCREEN_NAMES.SESSION,
    INFO_SCREEN: SCREEN_NAMES.INFO_SCREEN,
    DOCUMENT_CAPTURE_SCREEN: SCREEN_NAMES.DOCUMENT_CAPTURE_SCREEN,
    /** The camera's video stream actually started (the cross-browser permission-granted signal). */
    CAMERA_STREAM_STARTED: SCREEN_NAMES.CAMERA_STREAM_STARTED,
    /** The vendor's own capture UI has loaded and is interactive (Veriff STARTED / Regula
     *  onReady), before any camera access is requested. Distinct from CAMERA_STREAM_STARTED. */
    DOCUMENT_CAPTURE_READY: "document_capture_ready",
    /** Veriff-only: the user finished their side of the interaction and handed the captured
     *  images off to Veriff's own backend, before MESSAGES.FINISHED resolves. */
    DOCUMENT_CAPTURE_COMPLETED: "document_capture_completed",
    /** Capture succeeded; images are uploading and backend verification is in flight. */
    PROCESSING_SCREEN: SCREEN_NAMES.PROCESSING_SCREEN,
    /** The confirmation screen mounted — Legal ID and SSN Card. */
    CONFIRMATION_SCREEN: SCREEN_NAMES.CONFIRMATION_SCREEN,
    /** Face Verification only — the permission prompt (iProov `permission`, at startup before `ready`). */
    CAMERA_PERMISSION_SCREEN: SCREEN_NAMES.CAMERA_PERMISSION_SCREEN,
    /** Face Verification only — the recovery screen; its Reload button reloads the HOST page (VEX-7599). */
    CAMERA_PERMISSION_RECOVERY_SCREEN: "camera_permission_recovery_screen",
    /** Face Verification only — the face capture screen (iProov `started`, permission gates cleared). */
    FACE_CAPTURE_SCREEN: SCREEN_NAMES.FACE_CAPTURE_SCREEN,
    /** Face Verification only — iProov's `failed` slot, showing why the capture was rejected
     *  (VEX-7653). Viewed-only, and deliberately NOT an Outcome name: a capture can fail
     *  several times inside one launch, but only one Outcome may fire per launch, so this
     *  is what makes the per-attempt failure count (and the repeat-failure rate) legible.
     *  A journey that ends here files its Outcome under `face_capture_screen`, the
     *  `capture` section's name. */
    FACE_CAPTURE_FAILURE_SCREEN: "face_capture_failure_screen",
    /** Face Verification only — the pre-flight check returned a decisive negative (VEX-7617). Emitted
     *  whether or not we act on it, so report-only can measure the false-positive rate. */
    PREFLIGHT_UNSUPPORTED: "preflight_unsupported",
  },
  CLICKED: {
    START_DOCUMENT_CAPTURE: "start_document_capture",
    /** User indicated they don't have the document, on the pre-capture info screen. */
    REPORT_NO_DOCUMENT: "report_no_document",
    /** Close/cancel on the reader — Regula only; Veriff has no observable click for this. */
    CLOSE_DOCUMENT_CAPTURE: "close_document_capture",
    SWITCH_CAMERA: "switch_camera",
    TOGGLE_PROCESSING_ANIMATION: "toggle_processing_animation",
    /** "Confirm" on the confirmation screen — Legal ID and SSN Card. */
    CONFIRM_DOCUMENT: "confirm_document",
    /** "Retake" on the confirmation screen — Legal ID and SSN Card. */
    RETAKE_DOCUMENT: "retake_document",
    /** SSN Card only — "Join a video call" on the out-of-retakes variant
     *  (VEX-7961). Replaces RETAKE_DOCUMENT there: same downstream action
     *  (still reports `user_retake_requested`, IVA already routes it to
     *  human step-up at the cap), but a member acting on this honest label
     *  needs to be countable separately from a standard-screen Retake. */
    JOIN_VIDEO_CALL: "join_video_call",
    /** Face Verification only — starts the scan (iProov's `button` slot). */
    START_FACE_CAPTURE: "start_face_capture",
    /** Face Verification only — opens the biometric policy (consent flow). */
    OPEN_BIOMETRIC_POLICY: "open_biometric_policy",
    /** Face Verification only — opens the flashing-lights notice. */
    OPEN_FLASHING_LIGHTS_NOTICE: "open_flashing_lights_notice",
    /** Face Verification only — asks for the camera gate, triggering the device prompt. */
    ALLOW_CAMERA_ACCESS: "allow_camera_access",
    /** Face Verification only — re-requests a denied gate (camera OR motion) by reloading the host
     *  page, the only re-prompt that works on iOS Safari. */
    RETRY_PERMISSION: "retry_permission",
    /** Face Verification only — restarts the flow after a failure. */
    RETRY_FACE_CAPTURE: "retry_face_capture",
  },
} as const;

/**
 * Which `elementName` a section's `Client Outcome` is filed under, for the flows that
 * self-join view -> outcome (SSN Card, Legal ID). At most one Outcome fires per launch,
 * naming the stage the flow ended in: `passed` = the flow reached its actual terminal
 * success point (the backend answered for SSN Card; Confirm + PATCH succeeds for Legal
 * ID); `failed` = it never got there. `abandoned`/`fraudulent` are unused.
 *
 * Total over {@link OutcomeSection} so a lookup here is always safe to index — every
 * flow, verification included, resolves its Outcome's name through this table.
 *
 * A section being listed here does NOT mean a flow files there: each flow's own
 * stage -> section map returns `null` for stages that shouldn't conclude an attempt
 * (`info`, for instance, where both outcomes would restate an already-recorded Clicked).
 */
export const OUTCOME_ELEMENT_BY_SECTION = {
  /** Failed before any screen rendered — handshake, payload, or pre-render session fault. */
  [DOCUMENT_CAPTURE_SECTIONS.INIT]: SCREEN_NAMES.SESSION,
  [DOCUMENT_CAPTURE_SECTIONS.INFO]: SCREEN_NAMES.INFO_SCREEN,
  /** Failed on the capture surface (device, permission, quality, timeout, user cancel). */
  [DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE]: SCREEN_NAMES.DOCUMENT_CAPTURE_SCREEN,
  [DOCUMENT_CAPTURE_SECTIONS.CAMERA_PERMISSION]: SCREEN_NAMES.CAMERA_PERMISSION_SCREEN,
  [DOCUMENT_CAPTURE_SECTIONS.CAPTURE]: SCREEN_NAMES.FACE_CAPTURE_SCREEN,
  /** Upload/poll failed or the backend rejected the document (`failed`) — for both
   *  flows a `passed` backend response now moves on to CONFIRMATION instead of
   *  concluding here (VEX-7846). */
  [DOCUMENT_CAPTURE_SECTIONS.PROCESSING]: SCREEN_NAMES.PROCESSING_SCREEN,
  /** Confirm succeeds (`passed`) or Retake (`failed`) — Legal ID (Confirm+PATCH) and
   *  SSN Card (Confirm, no PATCH). */
  [DOCUMENT_CAPTURE_SECTIONS.CONFIRMATION]: SCREEN_NAMES.CONFIRMATION_SCREEN,
} as const;

/**
 * A section that can conclude an attempt. Flows type their stage -> section map with
 * this, so a section with no registered elementName is a compile error rather than a
 * silently dropped Outcome.
 */
export type OutcomeSection = keyof typeof OUTCOME_ELEMENT_BY_SECTION;

